// src/toolkitServices/stoneAttributeApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const stoneAttributeApi = createApi({
  reducerPath: "stoneAttributeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["StoneAttribute", "StoneAttributeGrouped"],
  endpoints: (builder) => ({
    // list with server-side filters & paging
    getStoneAttributes: builder.query({
      query: (params = {}) => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v === undefined || v === null || v === "") return;
          usp.set(k, v);
        });
        const qs = usp.toString();
        return `/stone-attributes${qs ? `?${qs}` : ""}`;
      },
      providesTags: (res) =>
        res?.data
          ? [
              ...res.data.map((r) => ({ type: "StoneAttribute", id: r._id })),
              { type: "StoneAttribute", id: "LIST" },
            ]
          : [{ type: "StoneAttribute", id: "LIST" }],
    }),

    // grouped active for dropdowns (used later in Stone Master)
    getAllGroupedStoneAttributes: builder.query({
      query: () => `/stone-attributes/all`,
      providesTags: [{ type: "StoneAttributeGrouped", id: "ALL" }],
    }),

    createStoneAttribute: builder.mutation({
      query: (body) => ({
        url: `/stone-attributes/create`,
        method: "POST",
        body,
      }),
      // ⚡ no invalidatesTags -> local patch instead
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          // backend best: { success, data }
          const created = res?.data || res;

          // patch all paged lists that match filters
          const subs =
            stoneAttributeApi.util.selectInvalidatedBy(getState(), [
              { type: "StoneAttribute", id: "LIST" },
            ]) || [];

          for (const { endpointName, originalArgs } of subs) {
            if (endpointName !== "getStoneAttributes") continue;

            const { type, activeOnly, page, limit } = originalArgs || {};
            const matchesType = !type || type === created.type;
            const matchesActive =
              !activeOnly || activeOnly === "" ? true : created.isActive;

            if (matchesType && matchesActive && (page ?? 1) === 1) {
              dispatch(
                stoneAttributeApi.util.updateQueryData(
                  "getStoneAttributes",
                  originalArgs,
                  (draft) => {
                    draft.data = draft.data || [];
                    // de-dup
                    if (!draft.data.find((x) => x._id === created._id)) {
                      draft.data.unshift(created);
                      if (limit && draft.data.length > limit) {
                        draft.data.pop();
                      }
                      draft.total = (draft.total || 0) + 1;
                    }
                  }
                )
              );
            }
          }

          // patch grouped cache (active only arrays)
          dispatch(
            stoneAttributeApi.util.updateQueryData(
              "getAllGroupedStoneAttributes",
              undefined,
              (draft) => {
                if (!draft || !created?.type) return;
                const key = created.type; // "shape" | "color" | "size" | "settingType"
                if (!draft[key]) return;
                const arr = draft[key];
                const idx = arr.findIndex((x) => x._id === created._id);
                if (created.isActive) {
                  if (idx === -1) arr.unshift(created);
                  else arr[idx] = created;
                } else if (idx !== -1) {
                  arr.splice(idx, 1);
                }
              }
            )
          );
        } catch {}
      },
    }),

    updateStoneAttribute: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/stone-attributes/${id}`,
        method: "PUT",
        body,
      }),
      // ⚡ no invalidatesTags -> local patch instead
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data || { _id: id, ...patch };

          const subs =
            stoneAttributeApi.util.selectInvalidatedBy(getState(), [
              { type: "StoneAttribute", id: "LIST" },
            ]) || [];

          for (const { endpointName, originalArgs } of subs) {
            if (endpointName !== "getStoneAttributes") continue;

            dispatch(
              stoneAttributeApi.util.updateQueryData(
                "getStoneAttributes",
                originalArgs,
                (draft) => {
                  if (!draft?.data) return;

                  const { type, activeOnly, page, limit } = originalArgs || {};
                  const matchesType = !type || type === updated.type;
                  const matchesActive =
                    !activeOnly || activeOnly === ""
                      ? true
                      : updated.isActive;

                  const idx = draft.data.findIndex((x) => x._id === id);

                  if (idx !== -1) {
                    // already present in this page
                    if (matchesType && matchesActive) {
                      draft.data[idx] = { ...draft.data[idx], ...updated };
                    } else {
                      // no longer belongs to this filtered list
                      draft.data.splice(idx, 1);
                      draft.total = Math.max(0, (draft.total || 0) - 1);
                    }
                  } else {
                    // was not present; if it now matches, optionally add to page 1
                    if (matchesType && matchesActive && (page ?? 1) === 1) {
                      draft.data.unshift(updated);
                      if (limit && draft.data.length > limit) draft.data.pop();
                      draft.total = (draft.total || 0) + 1;
                    }
                  }
                }
              )
            );
          }

          // patch grouped cache
          dispatch(
            stoneAttributeApi.util.updateQueryData(
              "getAllGroupedStoneAttributes",
              undefined,
              (draft) => {
                if (!draft || !updated?.type) return;
                const key = updated.type; // shape/color/size/settingType
                if (!draft[key]) return;
                const arr = draft[key];
                const i = arr.findIndex((x) => x._id === updated._id);
                if (updated.isActive) {
                  if (i === -1) arr.unshift(updated);
                  else arr[i] = { ...arr[i], ...updated };
                } else if (i !== -1) {
                  arr.splice(i, 1);
                }
              }
            )
          );
        } catch {}
      },
    }),

    deleteStoneAttribute: builder.mutation({
      query: (id) => ({
        url: `/stone-attributes/${id}`,
        method: "DELETE",
      }),
      // ⚡ no invalidatesTags -> local patch instead
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // optimistic remove from all cached pages
        const patches = [];
        const subs =
          stoneAttributeApi.util.selectInvalidatedBy(getState(), [
            { type: "StoneAttribute", id: "LIST" },
          ]) || [];

        for (const { endpointName, originalArgs } of subs) {
          if (endpointName !== "getStoneAttributes") continue;

          const patchResult = dispatch(
            stoneAttributeApi.util.updateQueryData(
              "getStoneAttributes",
              originalArgs,
              (draft) => {
                if (!draft?.data) return;
                const idx = draft.data.findIndex((x) => x._id === id);
                if (idx !== -1) {
                  draft.data.splice(idx, 1);
                  draft.total = Math.max(0, (draft.total || 0) - 1);
                }
              }
            )
          );
          patches.push(patchResult);
        }

        try {
          await queryFulfilled;
          // also nudge grouped cache (remove from all groups)
          dispatch(
            stoneAttributeApi.util.updateQueryData(
              "getAllGroupedStoneAttributes",
              undefined,
              (draft) => {
                if (!draft) return;
                ["shape", "color", "size", "settingType"].forEach((k) => {
                  if (!draft[k]) return;
                  const i = draft[k].findIndex((x) => x._id === id);
                  if (i !== -1) draft[k].splice(i, 1);
                });
              }
            )
          );
        } catch {
          // rollback optimistic updates
          patches.forEach((p) => p.undo && p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetStoneAttributesQuery,
  useGetAllGroupedStoneAttributesQuery,
  useCreateStoneAttributeMutation,
  useUpdateStoneAttributeMutation,
  useDeleteStoneAttributeMutation,
} = stoneAttributeApi;

export default stoneAttributeApi;
